import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

function unwrapData(body) {
  if (body != null && typeof body === "object" && "data" in body) {
    return body.data;
  }
  return body;
}

export const fetchCertificateTemplates = createAsyncThunk(
  "certificateTemplate/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/certificate-templates", { params });
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch templates",
      );
    }
  },
);

export const fetchCertificateTemplateById = createAsyncThunk(
  "certificateTemplate/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/certificate-templates/${id}`);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch template",
      );
    }
  },
);

export const fetchDefaultTemplateConfig = createAsyncThunk(
  "certificateTemplate/fetchDefaultConfig",
  async (type = "completion", { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/certificate-templates/defaults/config", {
        params: { type },
      });
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load default config",
      );
    }
  },
);

export const createCertificateTemplate = createAsyncThunk(
  "certificateTemplate/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/certificate-templates", data);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create template",
      );
    }
  },
);

export const updateCertificateTemplate = createAsyncThunk(
  "certificateTemplate/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/certificate-templates/${id}`, data);
      return unwrapData(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update template",
      );
    }
  },
);

export const deleteCertificateTemplate = createAsyncThunk(
  "certificateTemplate/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/certificate-templates/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete template",
      );
    }
  },
);

const certificateTemplateSlice = createSlice({
  name: "certificateTemplate",
  initialState: {
    templates: [],
    currentTemplate: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentTemplate(state) {
      state.currentTemplate = null;
    },
    clearTemplateError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificateTemplates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCertificateTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload || [];
      })
      .addCase(fetchCertificateTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCertificateTemplateById.fulfilled, (state, action) => {
        state.currentTemplate = action.payload;
      })
      .addCase(createCertificateTemplate.fulfilled, (state, action) => {
        if (action.payload) state.templates.unshift(action.payload);
      })
      .addCase(updateCertificateTemplate.fulfilled, (state, action) => {
        state.currentTemplate = action.payload;
        const idx = state.templates.findIndex((t) => t.id === action.payload?.id);
        if (idx >= 0) state.templates[idx] = action.payload;
      })
      .addCase(deleteCertificateTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter((t) => t.id !== action.payload);
      });
  },
});

export const { clearCurrentTemplate, clearTemplateError } =
  certificateTemplateSlice.actions;
export default certificateTemplateSlice.reducer;
